import { put, select } from 'redux-saga/effects';
import {
  SheetAndView,
  selectSheetAndView,
} from '../../../selectors/sheetSelector';
import {
  scrollViewportByButtons,
  setViewportFromScrollbarPosition,
} from './viewsActions';
import {
  getUnHiddenPosition,
  getViewportStartEnd,
} from '../../../utils/views/virtualViewport';
import {
  getViewportCols,
  getViewportRows,
} from '../../../utils/views/viewport';
import { Direction } from '../../../../windows/helpers/adjustWindow';
import { GridEntity, Viewport } from '../../../types';
import { updateSheetView } from '../../sheetsManagerSlice';
import { DataDirection } from '../../../../core/types/ui/position';

// #region scrollViewport By buttons & trackpad
// Double keys jumping to viewport start end
// Single Keys Row | Col
// trackpad scroll
export function* scrollViewportByButtonsMi({
  payload,
}: ReturnType<typeof scrollViewportByButtons>) {
  const {
    sheetId,
    windowWidth,
    windowHeight,
    direction,
    entityType,
    expectedIndex,
    skipIndexes = 1,
    scrollHorizontally,
    scrollVertically,
  } = payload;
  const { sheet, sheetView }: SheetAndView = yield select((state) =>
    selectSheetAndView(state, sheetId)
  );

  const sheetViewportView = {
    rowViewport: sheetView.rowViewport,
    colViewport: sheetView.colViewport,
    filterResults: sheetView.filterResults,
  };

  let colStartIndex = 0;
  let rowStartIndex = 0;
  if (expectedIndex !== undefined) {
    if (entityType === GridEntity.Column) colStartIndex = expectedIndex;
    else rowStartIndex = expectedIndex;
  } else {
    if (scrollHorizontally || entityType == GridEntity.Column) {
      const { start: colStart } = getViewportStartEnd(
        sheetView.colViewport.nonFrozenIndexes
      );
      colStartIndex = getUnHiddenPosition(
        sheet.colSizes,
        sheet.hiddenCols,
        colStart,
        sheet.maxColIndex,
        direction,
        sheet.freezedCols,
        undefined,
        skipIndexes
      );
    }

    if (scrollVertically || entityType === GridEntity.Row) {
      const { start: rowStart } = getViewportStartEnd(
        sheetView.rowViewport.nonFrozenIndexes
      );
      rowStartIndex = getUnHiddenPosition(
        sheet.rowSizes,
        sheet.hiddenRows,
        rowStart,
        sheet.maxRowIndex,
        direction,
        sheet.freezedRows,
        sheetView.filterResults,
        skipIndexes
      );
    }
  }

  let updatedColViewport: Viewport | undefined;
  let updatedRowViewport: Viewport | undefined;

  if (entityType === GridEntity.Column || scrollHorizontally) {
    updatedColViewport = getViewportCols(
      windowWidth,
      sheet,
      sheetViewportView,
      Direction.Right,
      {
        start: colStartIndex,
      }
    );
  }

  if (entityType === GridEntity.Row || scrollVertically) {
    const updates = getViewportRows(
      windowWidth,
      windowHeight,
      sheet,
      {
        ...sheetViewportView,
        colViewport: updatedColViewport ?? sheetView.colViewport,
      },
      Direction.Bottom,
      {
        start: rowStartIndex,
      }
    );
    if (updates.colViewport) updatedColViewport = updates.colViewport;
    updatedRowViewport = updates.rowViewport;
  }

  yield put(
    updateSheetView({
      sheetId,
      colViewport: updatedColViewport,
      rowViewport: updatedRowViewport,
    })
  );
}
// #endregion

// #region setIndexFromScrollbarPositionMi
export function* setViewportFromScrollbarPositionMi({
  payload,
}: ReturnType<typeof setViewportFromScrollbarPosition>) {
  const {
    sheetId,
    entityType = GridEntity.Column,
    windowWidth,
    windowHeight,
    expectedIndex = 0,
  } = payload;
  const { sheet, sheetView }: SheetAndView = yield select((state) =>
    selectSheetAndView(state, sheetId)
  );

  const sheetViewportView = {
    rowViewport: sheetView.rowViewport,
    colViewport: sheetView.colViewport,
    filterResults: sheetView.filterResults,
  };

  let updatedColViewport: Viewport | undefined;
  let updatedRowViewport: Viewport | undefined;

  if (entityType === GridEntity.Column) {
    const colStartIndex = getUnHiddenPosition(
      sheet.colSizes,
      sheet.hiddenCols,
      expectedIndex,
      sheet.maxColIndex,
      DataDirection.Forward,
      sheet.freezedCols,
      undefined,
      0
    );
    updatedColViewport = getViewportCols(
      windowWidth,
      sheet,
      sheetViewportView,
      Direction.Right,
      {
        start: colStartIndex,
      }
    );
  } else if (entityType === GridEntity.Row) {
    const rowStartIndex = getUnHiddenPosition(
      sheet.rowSizes,
      sheet.hiddenRows,
      expectedIndex,
      sheet.maxRowIndex,
      DataDirection.Forward,
      sheet.freezedRows,
      sheetView.filterResults,
      0
    );
    const viewportSet = getViewportRows(
      windowWidth,
      windowHeight,
      sheet,
      sheetViewportView,
      Direction.Bottom,
      {
        start: rowStartIndex,
      }
    );
    updatedColViewport = viewportSet.colViewport;
    updatedRowViewport = viewportSet.rowViewport;
  }

  yield put(
    updateSheetView({
      sheetId,
      colViewport: updatedColViewport,
      rowViewport: updatedRowViewport,
    })
  );
}
// #endregion
